/**
 * AffiliateApply.jsx
 *
 * Public application form for the influencer / affiliate program. Anyone
 * with an audience can apply — super-admin approves each one manually to
 * keep the brand quality high before they get a referral code.
 *
 * The form intentionally lives at /affiliates/apply (plural) so it pairs
 * naturally with /affiliates/dashboard for approved affiliates.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Megaphone, Sparkles, Wallet } from 'lucide-react';
import { api } from '@/lib/api';

export default function AffiliateApply() {
    const [form, setForm] = useState({
        name: '', email: '', paypal_email: '', audience_size: 0,
        primary_platform: 'Instagram', social_handles: '', pitch: '',
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState(null);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setErr(null); setBusy(true);
        try {
            await api.post('/affiliates/apply', {
                ...form,
                audience_size: parseInt(form.audience_size) || 0,
            });
            setDone(true);
        } catch (e2) {
            setErr(e2?.response?.data?.detail || 'Could not submit your application.');
        } finally { setBusy(false); }
    };

    if (done) {
        return (
            <main className="max-w-2xl mx-auto px-4 py-14" data-testid="affiliate-apply-done">
                <div className="bg-lq-success/10 border-2 border-lq-success/40 rounded-3xl p-8 text-center">
                    <CheckCircle2 size={48} className="mx-auto text-lq-success mb-4"/>
                    <h1 className="font-display text-3xl text-lq-ink-900 mb-2">Application received</h1>
                    <p className="text-lq-ink-700">We review every application by hand — usually within 48 hours. You'll get an email from <strong>hello@linguisticquest.com</strong> with your unique code once you're in.</p>
                    <Link to="/" className="mt-6 inline-flex px-6 py-3 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700">Back to home</Link>
                </div>
            </main>
        );
    }

    return (
        <main className="max-w-3xl mx-auto px-4 py-10" data-testid="affiliate-apply">
            <header className="mb-8">
                <h1 className="font-display text-4xl sm:text-5xl text-lq-ink-900">Become a Linguistic Quest affiliate</h1>
                <p className="mt-3 text-lg text-lq-ink-700">Earn 10% on every subscription you bring in. Your followers get 10% off Polyglot or Global. PayPal payouts the moment your balance hits £20.</p>
            </header>

            <div className="grid sm:grid-cols-3 gap-3 mb-8">
                <Bullet icon={<Sparkles size={20}/>} title="10% off for them" body="Your followers save £0.45/mo on Polyglot or £0.60/mo on Global."/>
                <Bullet icon={<Wallet size={20}/>} title="10% to you" body="On every successful billing cycle, paid via PayPal."/>
                <Bullet icon={<Megaphone size={20}/>} title="Real impact" body="Help families keep their heritage language alive."/>
            </div>

            <form onSubmit={submit} className="bg-white rounded-2xl border border-lq-ink-100 p-6 space-y-4" data-testid="affiliate-apply-form">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Your full name" required>
                        <input name="name" value={form.name} onChange={set('name')} required maxLength={80} className="input" data-testid="apply-name"/>
                    </Field>
                    <Field label="Contact email" required hint="We send your code here.">
                        <input name="email" type="email" value={form.email} onChange={set('email')} required className="input" data-testid="apply-email"/>
                    </Field>
                    <Field label="PayPal email" required hint="Payouts arrive here. Can be the same as above.">
                        <input name="paypal_email" type="email" value={form.paypal_email} onChange={set('paypal_email')} required className="input" data-testid="apply-paypal"/>
                    </Field>
                    <Field label="Primary platform" required>
                        <select name="primary_platform" value={form.primary_platform} onChange={set('primary_platform')} required className="input" data-testid="apply-platform">
                            {['Instagram','TikTok','YouTube','Blog','Podcast','Newsletter','Twitter / X','Other'].map(p => <option key={p}>{p}</option>)}
                        </select>
                    </Field>
                    <Field label="Audience size (approx)" required>
                        <input name="audience_size" type="number" min={0} value={form.audience_size} onChange={set('audience_size')} required className="input" data-testid="apply-audience"/>
                    </Field>
                    <Field label="Social handles" hint="Comma-separated. We'll check these.">
                        <input name="social_handles" value={form.social_handles} onChange={set('social_handles')} maxLength={400} placeholder="@maryigtm, youtube.com/@mary" className="input" data-testid="apply-handles"/>
                    </Field>
                </div>
                <Field label="Why are you a fit?" hint="A sentence or two helps us prioritise.">
                    <textarea name="pitch" value={form.pitch} onChange={set('pitch')} maxLength={1000} rows={4} className="input" data-testid="apply-pitch"/>
                </Field>

                {err && <div className="bg-lq-error/10 border border-lq-error/40 text-lq-error rounded-lg px-3 py-2 text-sm" data-testid="apply-error">{err}</div>}

                <button type="submit" disabled={busy} className="w-full px-6 py-3 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700 disabled:opacity-50" data-testid="apply-submit">
                    {busy ? 'Submitting…' : 'Apply to join'}
                </button>
                <p className="text-xs text-lq-ink-500 text-center">
                    By applying you agree to the <Link to="/legal/affiliate-agreement" className="underline text-lq-purple-700 hover:text-lq-purple-900" data-testid="apply-terms-link">Affiliate Programme Agreement</Link>.
                </p>
            </form>

            {/* Quick-glance terms summary — full legal copy lives on /legal/affiliate-agreement */}
            <section className="mt-10 bg-lq-purple-50 border border-lq-purple-100 rounded-2xl p-5 text-sm" data-testid="apply-terms-summary">
                <h2 className="font-display text-lg text-lq-ink-900 mb-2">Key terms at a glance</h2>
                <ul className="space-y-1.5 text-lq-ink-700 list-disc pl-5">
                    <li><strong>10% commission</strong> on every successful Polyglot or Global billing cycle. Free + Explorer tiers excluded.</li>
                    <li><strong>10% off</strong> for your followers on Polyglot or Global. Auto-applied at checkout when they use your link.</li>
                    <li><strong>60-day holding period</strong> — commission on each referred customer's payments is held for 60 days from their first payment (≈ two billing cycles) before being released. Protects against refunds, chargebacks and quick cancellations.</li>
                    <li><strong>Clawback on cancellation</strong> — if a customer cancels or refunds within the 60-day holding window, the related commission is reversed. After day 60 it's locked in.</li>
                    <li><strong>£20 minimum payout</strong>, paid monthly via PayPal. Balances below £20 roll over.</li>
                    <li><strong>45-day residual release</strong> — if your balance has sat below £20 for 45 days you can request release of the residual on the next monthly run.</li>
                    <li>30-day cookie attribution window from each click on your link.</li>
                </ul>
                <p className="mt-3 text-xs text-lq-ink-500">This is a summary only. The <Link to="/legal/affiliate-agreement" className="underline text-lq-purple-700" data-testid="apply-summary-terms-link">full Affiliate Programme Agreement</Link> governs the relationship.</p>
            </section>

            <style>{`.input{margin-top:4px;width:100%;border-radius:10px;border:1px solid #C7C1D1;padding:8px 12px;background:white}`}</style>
        </main>
    );
}

const Bullet = ({ icon, title, body }) => (
    <div className="bg-lq-purple-50 rounded-2xl p-4">
        <div className="text-lq-purple-700 mb-2">{icon}</div>
        <div className="font-semibold text-lq-ink-900 text-sm">{title}</div>
        <div className="text-xs text-lq-ink-700 mt-1">{body}</div>
    </div>
);

const Field = ({ label, hint, required, children }) => (
    <label className="block text-sm">
        <span className="text-lq-ink-900 font-medium">{label}{required && <span className="text-lq-error">*</span>}</span>
        {children}
        {hint && <span className="text-xs text-lq-ink-500 mt-1 block">{hint}</span>}
    </label>
);
